import React, {
  FunctionComponent,
  useCallback,
  useState,
  ReactNode,
} from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { useWindowSize, useAsyncEffect } from '../../hooks';
import { List } from '../List';
import { FilesProps, FileItemProps } from './Files.types';
import { Button } from '../Button';
import { Group } from '../Group';
import { withAlpha } from '../../util/color.util';

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  background-color: white;
  border-radius: 6px;

  .accepted-files {
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0em;
    text-align: center;
  }

  .accepted-files-title {
    overflow: visible;
  }

  button {
    min-width: 20px;
    margin: 0px;
  }

  > *:last-child {
    border: 1px solid lightgray;
  }

  @media (min-width: 1000px) {
    height: 180px;

    * {
      overflow: hidden;
    }

    > *:last-child {
      flex: 70%;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }

  @media (max-width: 1000px) {
    flex-direction: column;

    > *:last-child {
      flex-grow: 1;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }
`;

const Dropzone = styled.div`
  border: 1px dashed lightgray;

  color: ${(props: any) => withAlpha(props.theme.colors.primaryColor, 0.2)};

  @media (min-width: 1000px) {
    flex: 30%;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  @media (max-width: 1000px) {
    flex-grow: 1;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  > div {
    height: 100%;
    padding: 0.5rem;

    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;
    cursor: pointer;

    i {
      font-size: 500%;

      @media (max-width: 1000px) {
        font-size: 200%;
      }
    }
  }
`;

const FileListItem: FunctionComponent<FileItemProps> = props => {
  const { index, file, onDownload, onDelete } = props;
  const [state, setState] = useState<'idle' | 'deleting' | 'downloading'>(
    'idle',
  );

  useAsyncEffect(
    async isMounted => {
      switch (state) {
        case 'downloading': {
          if (onDownload) {
            const p = onDownload(file, index);
            if (p instanceof Promise) await p;
          }
          break;
        }
        case 'deleting': {
          if (onDelete) {
            const p = onDelete(file, index);
            if (p instanceof Promise) await p;
          }
          break;
        }
        case 'idle':
          break;
      }

      if (isMounted() && state !== 'idle') {
        setState('idle');
      }
    },
    [state],
  );

  return (
    <li key={index}>
      <Group justify="space-between">
        <Button
          variant="link"
          style={{
            cursor: props.onDownload ? 'pointer' : 'not-allowed',
          }}
          icon={state === 'downloading' && 'fas fa-circle-notch fa-spin'}
          onClick={async e => {
            if (!onDownload) return;
            setState('downloading');
            e.stopPropagation();
          }}
        >
          <span>
            {file.name}{' '}
            {file.meta && file.meta.filesize && `(${file.meta.filesize})`}
          </span>
        </Button>
        {props.onDelete && (
          <Button
            icon={
              state === 'deleting'
                ? 'fas fa-circle-notch fa-spin'
                : 'fas fa-trash'
            }
            motif="destructive"
            size="tiny"
            variant="outline"
            disabled={state !== 'idle'}
            onClick={e => {
              e.stopPropagation();
              setState('deleting');
            }}
          />
        )}
      </Group>
    </li>
  );
};

export const Files: FunctionComponent<
  FilesProps & React.HTMLProps<HTMLInputElement>
> = props => {
  const { isMobile } = useWindowSize();
  const [isUploading, setIsUploading] = useState(false);
  const { onDrop, accept } = props;

  const handleDrop = useCallback(
    (files: File[]) => {
      const p = onDrop(files);

      if (p instanceof Promise) {
        (async function () {
          setIsUploading(true);
          await p;
          setIsUploading(false);
        })();
      }
    },
    [onDrop],
  );

  const dropzone = useDropzone({
    onDrop: handleDrop,
    multiple: true,
  });

  const renderContent = (): ReactNode => {
    if (dropzone.isDragActive) {
      return (
        <div>
          <i className="fas fa-inbox" />
          <span>Drop to upload</span>
        </div>
      );
    } else if (isUploading) {
      return (
        <div>
          <i className="fas fa-circle-notch fa-spin" />
          <span>Uploading...</span>
        </div>
      );
    } else {
      return (
        <div>
          <i className="fas fa-cloud-upload-alt" />
          {isMobile ? (
            <span>Tap to upload</span>
          ) : (
            <span className="accepted-files-title">
              Click or drop here to upload
            </span>
          )}
          <p>
            <span className="accepted-files">
              Accepted file types: JPEG, JPG, PNG, PDF
            </span>
          </p>
        </div>
      );
    }
  };
  return (
    <Container>
      <Dropzone {...dropzone.getRootProps()}>
        <input
          {...dropzone.getInputProps()}
          data-test-id="document-upload-file-selector"
          accept={accept ? accept : '*'}
        />
        {renderContent()}
      </Dropzone>
      {props.files && props.files.length ? (
        <List>
          {props.files.map((file, index) => (
            <FileListItem
              key={index}
              index={index}
              file={file}
              onDownload={props.onDownload}
              onDelete={props.onDelete}
            />
          ))}
        </List>
      ) : null}
    </Container>
  );
};
